import {css} from '@emotion/react';
import {Flex, Grid, Group, Stack, Text} from '@mantine/core';
import {Dispatch, FC, SetStateAction, useEffect} from 'react';
import {DefaultValues, SubmitHandler, UseFormReset} from 'react-hook-form';
import {z} from 'zod';
import {CodesService} from '../api/generated/CodesService';
import {
  FilterOnOptions,
  JobQuoteStatusTypes,
  JobStatusTypes,
  JobTypes,
  ReviewStatuses,
  Roles,
} from '../api/generated/enums';
import {JobsService} from '../api/generated/JobsService';
import {PartnerSitesService} from '../api/generated/PartnerSitesService';
import {useRequireAuthorization} from '../auth/require-authorization';
import {createForm} from '../forms';
import {AdditionalParams} from '../hooks/paged-data-table/types';
import {useOptions} from '../hooks/use-options';
import {mapNullToUndefined, mapStringNumberToNumber} from '../utils/zod';

type FilterState = AdditionalParams<typeof JobsService.getAll>;

export type ComponentState = {
  filter: FilterState;
  tableState?: Parameters<typeof JobsService.getAll>[0];
};

const filterSchema = z.object({
  id: mapStringNumberToNumber(z.number().optional()),
  createdDate: mapNullToUndefined(z.date().optional()),
  partnerSiteId: mapNullToUndefined(z.number().optional()),
  assetNumber: mapNullToUndefined(z.string().optional()),
  certScrapReport: mapNullToUndefined(z.string().optional()),
  clientName: mapNullToUndefined(z.string().optional()),
  closedArchivedFromDate: mapNullToUndefined(z.date().optional()),
  closedArchivedToDate: mapNullToUndefined(z.date().optional()),
  createdFromDate: mapNullToUndefined(z.date().optional()),
  createdToDate: mapNullToUndefined(z.date().optional()),
  evaluationCompleteFromDate: mapNullToUndefined(z.date().optional()),
  evaluationCompleteToDate: mapNullToUndefined(z.date().optional()),
  evaluationIsRepairable: mapNullToUndefined(z.string().optional()),
  evaluationQuoteStatus: mapNullToUndefined(z.string().optional()),
  jobStatus: mapNullToUndefined(z.string().optional()),
  levelOfServiceCodeId: mapNullToUndefined(z.number().optional()),
  partNumber: mapNullToUndefined(z.string().optional()),
  reference1: mapNullToUndefined(z.string().optional()),
  reviewStatus: mapNullToUndefined(z.string().optional()),
  serialNumber: mapNullToUndefined(z.string().optional()),
  jobType: mapNullToUndefined(z.string().optional()),
  isHistory: mapNullToUndefined(z.boolean().optional()),
}) satisfies z.Schema<FilterState>;

export type FilterSchema = z.TypeOf<typeof filterSchema>;

const Form = createForm<FilterSchema>();

type TableFiltersProps = {
  defaultValues?: DefaultValues<FilterSchema>;
  clear: boolean;
  setClear: Dispatch<SetStateAction<boolean>>;
  onSubmit: SubmitHandler<FilterSchema>;
  userPartnerSiteIds?: number[] | null | undefined;
};

export const TableFilters: FC<TableFiltersProps> = ({
  clear,
  setClear,
  defaultValues,
  onSubmit,
  userPartnerSiteIds,
}) => {
  return (
    <Form
      onSubmit={onSubmit}
      schema={filterSchema}
      defaultValues={defaultValues}
      css={formCss}
    >
      {({reset}) => (
        <FormFields
          clear={clear}
          setClear={setClear}
          reset={reset}
          userPartnerSiteIds={userPartnerSiteIds}
        />
      )}
    </Form>
  );
};

const CertScrapReportIssuedOptions = [FilterOnOptions.Yes, FilterOnOptions.No];

type FormFieldsProps = {
  clear: boolean;
  setClear: Dispatch<SetStateAction<boolean>>;
  reset: UseFormReset<FilterSchema>;
  userPartnerSiteIds?: number[] | null | undefined;
};

const FormFields: FC<FormFieldsProps> = ({
  clear,
  setClear,
  reset,
  userPartnerSiteIds,
}) => {
  const isAuthorized = useRequireAuthorization({role: [Roles['Global Admin']]});
  const levelOfServiceCodeOptions =
    useOptions(CodesService.getLevelOfServiceCodeOptions).value ?? [];
  const partnerSiteIdOptions =
    useOptions(PartnerSitesService.getOptions).value ?? [];

  useEffect(() => {
    reset();
    setClear(false);
  }, [clear, reset, setClear]);

  return (
    <Stack w='100%' gap={6}>
      <Form.Row>
        <Form.TextInput label='Job ID' name='id' placeholder='12345' />
        <Form.Select
          name='jobStatus'
          label='Job Status'
          enumObject={JobStatusTypes}
          placeholder='All'
          clearable
        />
        <Form.Select
          label='Is Repairable'
          name='evaluationIsRepairable'
          enumObject={FilterOnOptions}
          placeholder='All'
        />
        <Form.TextInput
          name='serialNumber'
          label='Serial Number'
          placeholder='12345'
        />
      </Form.Row>
      <Form.Row>
        <Form.TextInput
          name='partNumber'
          label='Part Number'
          placeholder='12345'
        />
        <Form.Select
          clearable
          searchable
          name='levelOfServiceCodeId'
          label='Level of Service'
          placeholder='All'
          data={levelOfServiceCodeOptions}
        />
        <Form.TextInput
          name='assetNumber'
          label='Asset Number'
          placeholder='12345'
        />
        <Form.TextInput
          name='clientName'
          label='Client Name'
          placeholder='John Smith'
        />
      </Form.Row>
      <Form.Row>
        <Form.TextInput
          name='reference1'
          label='Reference 1'
          placeholder='12345'
        />
        <Form.Select
          name='evaluationQuoteStatus'
          label='Quote Status'
          enumObject={JobQuoteStatusTypes}
          placeholder='All'
          clearable
        />
        <Form.Select
          name='reviewStatus'
          label='Review Status'
          enumObject={ReviewStatuses}
          placeholder='All'
          clearable
        />
        <Form.Select
          label='Cert/Scrap Report Issued'
          name='certScrapReport'
          enumObject={CertScrapReportIssuedOptions}
          placeholder='All'
          clearable
        />
      </Form.Row>
      <Form.Row>
        <Form.Select
          name={'jobType'}
          enumObject={JobTypes}
          label={'Job Type'}
          placeholder={'Job Type'}
          clearable
        />
        <div />
        <div />
        <div />
      </Form.Row>
      <Grid>
        <Grid.Col span={3}>
          <Flex>
            <Group w='50%' grow>
              <Form.DatePickerInput
                name='evaluationCompleteFromDate'
                label={
                  <Text fw='400' fz='sm'>
                    <strong>Evaluation Date</strong>
                    <br />
                    From
                  </Text>
                }
                placeholder='January 1, 2000'
                clearable
              />
            </Group>
            <Text m='auto 4 4 4' flex={0}>
              &mdash;
            </Text>
            <Group w='50%' grow>
              <Form.DatePickerInput
                name='evaluationCompleteToDate'
                label={
                  <Text fw='400' fz='sm'>
                    <br />
                    To
                  </Text>
                }
                placeholder='January 1, 2000'
                clearable
              />
            </Group>
          </Flex>
        </Grid.Col>
        <Grid.Col span={3}>
          <Flex>
            <Group w='50%' grow>
              <Form.DatePickerInput
                name='closedArchivedFromDate'
                label={
                  <Text fw='400' fz='sm'>
                    <strong>Archived Date</strong>
                    <br />
                    From
                  </Text>
                }
                placeholder='January 1, 2000'
                clearable
              />
            </Group>
            <Text m='auto 4 4 4' flex={0}>
              &mdash;
            </Text>
            <Group w='50%' grow>
              <Form.DatePickerInput
                name='closedArchivedToDate'
                label={
                  <Text fw='400' fz='sm'>
                    <br />
                    To
                  </Text>
                }
                placeholder='January 1, 2000'
                clearable
              />
            </Group>
          </Flex>
        </Grid.Col>
        <Grid.Col span={3}>
          <Flex>
            <Group w='50%' grow>
              <Form.DatePickerInput
                name='createdFromDate'
                label={
                  <Text fw='400' fz='sm'>
                    <strong>Job Created</strong>
                    <br />
                    From
                  </Text>
                }
                placeholder='January 1, 2000'
                clearable
              />
            </Group>
            <Text m='auto 4 4 4' flex={0}>
              &mdash;
            </Text>
            <Group w='50%' grow>
              <Form.DatePickerInput
                name='createdToDate'
                label={
                  <Text fw='400' fz='sm'>
                    <br />
                    To
                  </Text>
                }
                placeholder='January 1, 2000'
                clearable
              />
            </Group>
          </Flex>
        </Grid.Col>
        <Grid.Col span={3}>
          {(isAuthorized || (userPartnerSiteIds?.length || 0) > 1) && (
            <Form.Select
              name='partnerSiteId'
              label={
                <Text fw='600' fz='sm'>
                  <br />
                  Partner Site
                </Text>
              }
              placeholder='All'
              data={partnerSiteIdOptions}
              clearable
            />
          )}
        </Grid.Col>
      </Grid>
      <Group ml='auto' mt='sm'>
        <Form.Submit>Apply</Form.Submit>
      </Group>
    </Stack>
  );
};

const formCss = css({
  '& > *': {
    marginTop: 8,
  },
});